// src/pages/Resources/AddUserModal.tsx
import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { searchUsers } from "@/services/resourcesApi";

interface User {
  id: string;
  displayName: string;
  email: string;
  upn: string;
}

interface AddUserModalProps {
  show: boolean;
  onHide: () => void;
  onAddUser: (userId: string, role: "Owner" | "Member") => Promise<void>;
  groupId: string | null; // Reserved for future use
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  show,
  onHide,
  onAddUser,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedRole, setSelectedRole] = useState<"Owner" | "Member">("Member");
  const [adding, setAdding] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const data = await searchUsers(searchQuery);
      setSearchResults(data);
    } catch (error) {
      console.error("Failed to search users:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddUser = async (user: User) => {
    setAdding(true);
    try {
      await onAddUser(user.id, selectedRole);
      setSearchQuery("");
      setSearchResults([]);
      onHide();
    } catch (error) {
      console.error("Failed to add user:", error);
      // Parent component will show error modal
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedRole("Member");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Search for user</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Enter name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
            >
              {searching ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                  Searching...
                </>
              ) : (
                <>
                  <FaSearch className="me-1" /> Search
                </>
              )}
            </Button>
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Add as</Form.Label>
          <Form.Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as "Owner" | "Member")}
          >
            <option value="Member">Member</option>
            <option value="Owner">Owner</option>
          </Form.Select>
        </Form.Group>

        {searching && (
          <div className="text-center my-3">
            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
            <span className="text-muted">Searching...</span>
          </div>
        )}

        {!searching && searchResults.length > 0 && (
          <div className="search-results mt-3">
            <div className="mb-2 fw-semibold">Search Results</div>
            <div className="list-group">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div className="fw-semibold">{user.displayName}</div>
                    <div className="text-muted small">{user.email}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="success"
                    onClick={() => handleAddUser(user)}
                    disabled={adding}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!searching && searchQuery && searchResults.length === 0 && (
          <div className="text-center text-muted my-3">No users found</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;
